import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction,
  Update,
} from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

import { handleError } from "utils/handleError";
import {
  CHANGE_COLLAB_STATE_ROUTINE,
  BULK_CHANGE_COLLAB_STATE_ROUTINE,
} from "constants/ActionTypes";
import {
  fetchCalendar,
  removePastQueue,
  removeSchedule,
  removeScheduledQueue,
  scheduleDraft,
  updateScheduledPost,
  sendCalendarFeedback,
  deleteDraft,
  updateDraftPost,
} from "api/calendar";

import {
  moveQueueToSchedule,
  moveQueueToDraft,
  moveScheduleToDraft,
  editDraft,
} from "api/post";

import {
  CalendarPostType,
  InitialCalendarStateType,
  RequestPostsOptionsTypes,
} from "./types";
import { NgPost } from "../post/format-post";
import { Post } from "../post";
import { standardizationPostData } from "utils/posts";

// Helper to create range key from start/end timestamps
export function createRangeKey(start: number, end: number): string {
  return `${start}-${end}`;
}

// Helper to parse range key back to start/end
export function parseRangeKey(rangeKey: string): {
  start: number;
  end: number;
} {
  const [start, end] = rangeKey.split("-").map(Number);
  return { start, end };
}

// Helper to check if range keys are adjacent (within 1 month)
export function areRangesAdjacent(key1: string, key2: string): boolean {
  const range1 = parseRangeKey(key1);
  const range2 = parseRangeKey(key2);
  const oneMonthMs = 31 * 24 * 60 * 60 * 1000; // ~1 month in milliseconds

  // Check if ranges overlap or are within 1 month of each other
  return Math.abs(range1.start - range2.start) < oneMonthMs;
}

// Resolve the rangeKey from a fetchCalendarPosts arg in a single place.
// Used by both the thunk and the .pending handler so they can't drift.
export function getRangeKeyFromArg(arg: {
  rangeKey?: string;
  start?: number;
  end?: number;
}): string | null {
  if (arg.rangeKey) return arg.rangeKey;
  if (arg.start && arg.end) return createRangeKey(arg.start, arg.end);
  return null;
}

export const fetchCalendarPosts = createAsyncThunk<
  { posts: CalendarPostType[]; rangeKey: string; replaceAll?: boolean },
  RequestPostsOptionsTypes & { rangeKey?: string; replaceAll?: boolean }
>(
  "calendar/fetchCalendarPosts",
  async (data: any, { rejectWithValue }: any) => {
    try {
      const result = await fetchCalendar(data);
      const posts = await Promise.all(
        ((result?.data?.posts || []) as unknown as Post[]).map(
          standardizationPostData
        )
      );

      // Generate rangeKey from start/end if not provided
      const rangeKey =
        getRangeKeyFromArg(data) || createRangeKey(data.start, data.end);

      return { posts, rangeKey, replaceAll: data.replaceAll };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updatePost = createAsyncThunk<string, any>(
  "calendar/updatePost",
  async (data, { rejectWithValue }) => {
    const { postId, post } = data;

    try {
      if (post.queued) {
        const result = await moveQueueToSchedule({ ...post, _id: postId });

        return result?.data[0];
      } else {
        const result = await updateScheduledPost(postId, post);
        return result?.data;
      }
    } catch (err) {
      // @ts-ignore
      return rejectWithValue(err.response.data);
    }
  }
);

export const updateDraft = createAsyncThunk<string, any>(
  "calendar/updateDraft",
  async (post, { rejectWithValue }) => {
    try {
      const result = await updateDraftPost(post);

      return result?.data;
    } catch (err) {
      // @ts-ignore
      return rejectWithValue(err.response.data);
    }
  }
);

export const scheduleDraftPost = createAsyncThunk<Post, any>(
  "calendar/scheduleDraftPost",
  async (post, { rejectWithValue }) => {
    try {
      const result = await scheduleDraft(post);
      // Standardize so the fulfilled reducer can upsert directly into the adapter
      const standardized = await standardizationPostData(result?.data);
      return standardized;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteDraftPost = createAsyncThunk<string, any>(
  "calendar/deleteDraftPost",
  async (data, { rejectWithValue }) => {
    try {
      const result = await deleteDraft(data.postId);

      return result?.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const moveToDraft = createAsyncThunk<string, any>(
  "calendar/moveToDraft",
  async (post: NgPost): Promise<any> => {
    if (!post.queued) {
      return await moveScheduleToDraft(post._id);
    }

    const newId = await moveQueueToDraft(post._id);
    // @ts-ignore
    return await editDraft({
      ...post,
      accountIds: Array.isArray(post.accountIds)
        ? post.accountIds
        : [post.accountId],
      _id: newId,
    });
  }
);

type RemoveScheduledPostError = {
  response: {
    status?: number;
    data?: unknown;
  };
};

export const removeScheduledPost = createAsyncThunk<
  { data: unknown },
  { postId: string; isQueue: boolean; isActive: boolean },
  { rejectValue: RemoveScheduledPostError }
>(
  "calendar/removePost",
  async ({ postId, isQueue, isActive }, { rejectWithValue }) => {
    try {
      if (isQueue) {
        return isActive
          ? await removeScheduledQueue(postId)
          : await removePastQueue(postId);
      }
      return await removeSchedule(postId, isActive);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          response: {
            status: error.response?.status,
            data: error.response?.data,
          },
        });
      }
      throw error;
    }
  }
);

export const calendarFeedback = createAsyncThunk<string, any>(
  "calendar/feedback",
  // @ts-ignore
  async ({ feedback }: { feedback: string }) =>
    await sendCalendarFeedback({ feedback })
);

const calendarAdapter = createEntityAdapter<CalendarPostType, string>({
  sortComparer: (a: CalendarPostType, b: CalendarPostType) =>
    a._id.localeCompare(b._id),
  selectId: (item) => item._id,
});

const initialState = {
  isLoading: false,
  ids: [],
  entities: {},
  cachedRanges: {},
  currentRangeKey: null,
  latestRequestId: {},
} as InitialCalendarStateType;

export const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    changePostProperty: (
      state: InitialCalendarStateType,
      action: PayloadAction<{ postId: string; key: string; value: any }>
    ) => {
      const { postId, key, value } = action.payload;
      // @ts-ignore
      calendarAdapter.updateOne(state, {
        id: postId,
        changes: { [key]: value },
      });
    },
    updateEntity: (
      state: InitialCalendarStateType,
      action: PayloadAction<Update<CalendarPostType, string>>
    ) => {
      calendarAdapter.updateOne(state, action.payload);
    },
    clearPosts: (state: InitialCalendarStateType) => {
      calendarAdapter.removeAll(state);
      state.cachedRanges = {};
      state.currentRangeKey = null;
      state.latestRequestId = {};
    },
    // Remove a single post from the Redux entity adapter by _id.
    // Needed in two scenarios:
    //   1. Identity change — draft→schedule or queue→schedule creates a new entity
    //      with a new _id, leaving the old _id stale in the store.
    //   2. Simple removal — scheduled→draft or delete removes the post from the
    //      calendar entirely; the old entity must be purged so it doesn't reappear.
    // Note: loadPosts(true) would eventually self-heal via the replaceAll path
    // (which diffs staleIds and calls removeMany), but removePost closes the
    // timing window between the user action and that API response arriving —
    // without it, the stale entity briefly reappears as a ghost duplicate.
    removePost: (
      state: InitialCalendarStateType,
      action: PayloadAction<string>
    ) => {
      calendarAdapter.removeOne(state, action.payload);
    },
    // Replace all posts in the entity adapter with new data from react-query.
    // Used by the query hook write-through to keep Redux in sync.
    setAllPosts: (
      state: InitialCalendarStateType,
      action: PayloadAction<CalendarPostType[]>
    ) => {
      calendarAdapter.setAll(state, action.payload);
      state.isLoading = false;
    },
    // Upsert posts without removing existing ones (for additive updates).
    upsertPosts: (
      state: InitialCalendarStateType,
      action: PayloadAction<CalendarPostType[]>
    ) => {
      calendarAdapter.upsertMany(state, action.payload);
      state.isLoading = false;
    },
  },
  extraReducers: (builder: any) => {
    builder.addCase(
      fetchCalendarPosts.fulfilled,
      (
        state: InitialCalendarStateType,
        action: {
          payload: {
            posts: CalendarPostType[];
            rangeKey: string;
            replaceAll?: boolean;
          };
          meta?: { requestId?: string };
        }
      ) => {
        const { posts, rangeKey, replaceAll } = action.payload;

        // Drop stale out-of-order responses for the same rangeKey. Only gates
        // when both the slice and the action carry a requestId — otherwise
        // pass through (covers tests and any direct dispatches).
        const latestRequestId = state.latestRequestId[rangeKey];
        const incomingRequestId = action.meta?.requestId;
        if (
          latestRequestId &&
          incomingRequestId &&
          incomingRequestId !== latestRequestId
        ) {
          return;
        }

        if (replaceAll) {
          // Replace posts when filters/accounts change to remove stale entries.
          // Use upsertMany + removeMany instead of setAll to avoid a flash
          // where all entities are cleared before the new ones are inserted.
          const freshIds = new Set<string>(posts.map((p) => p._id));
          const staleIds = state.ids.filter((id) => !freshIds.has(id));
          calendarAdapter.upsertMany(state, posts);
          if (staleIds.length) {
            calendarAdapter.removeMany(state, staleIds);
          }
          state.cachedRanges = {};
        } else {
          // Upsert posts for range navigation to preserve adjacent ranges.
          // NOTE: upsertMany is additive-only — it will NOT remove entities
          // that are no longer returned by the API. When a post's _id changes
          // (e.g. draft→schedule) or is deleted (e.g. moved to drafts), callers
          // must dispatch removePost(oldId) to avoid ghost duplicates.
          calendarAdapter.upsertMany(state, posts);
        }

        // Track this range as cached
        state.cachedRanges[rangeKey] = {
          start: parseRangeKey(rangeKey).start,
          end: parseRangeKey(rangeKey).end,
          fetchedAt: Date.now(),
        };
        state.currentRangeKey = rangeKey;

        // Clean up ranges that aren't adjacent to current range (keep 3-month window)
        const rangesToKeep = Object.keys(state.cachedRanges).filter(
          (key) => key === rangeKey || areRangesAdjacent(key, rangeKey)
        );

        // Remove post IDs that are outside the window
        const rangeKeysToDelete = Object.keys(state.cachedRanges).filter(
          (key) => !rangesToKeep.includes(key)
        );

        // Remove posts from deleted ranges. Also drop the matching
        // latestRequestId entries so the two maps stay in sync — otherwise
        // long sessions accumulate stale requestId entries indefinitely.
        rangeKeysToDelete.forEach((key) => {
          delete state.cachedRanges[key];
          delete state.latestRequestId[key];
        });

        // Note: We don't remove posts from entities here because
        // posts may span multiple ranges. Instead, rely on TTL-based cleanup
        // or allow natural accumulation with the understanding that memory leaks
        // from Batches 1-5 are the primary fixes.

        state.isLoading = false;
      }
    );
    builder.addCase(
      fetchCalendarPosts.pending,
      (
        state: InitialCalendarStateType,
        action: {
          meta: {
            arg: { rangeKey?: string; start?: number; end?: number };
            requestId: string;
          };
        }
      ) => {
        state.isLoading = true;

        // Track the most recent requestId per rangeKey so the .fulfilled
        // handler can drop stale responses (e.g. an immediate post-save
        // refetch that resolves AFTER the 8s deferred post-publish refetch
        // would otherwise overwrite the newer FAILED/SUCCESS state).
        const rangeKey = getRangeKeyFromArg(action.meta.arg || {});
        if (rangeKey) {
          state.latestRequestId[rangeKey] = action.meta.requestId;
        }
      }
    );
    builder.addCase(
      fetchCalendarPosts.rejected,
      (state: InitialCalendarStateType, action: PayloadAction<string>) => {
        calendarAdapter.setAll(state, []);
        handleError(action.payload, "There was an error");
        state.isLoading = false;
      }
    );

    builder.addCase(
      updatePost.rejected,
      (_: any, action: PayloadAction<string>) => {
        handleError(action.payload, "There was an error");
      }
    );
    builder.addCase(
      scheduleDraftPost.pending,
      (state: InitialCalendarStateType) => {
        state.isLoading = true;
      }
    );
    builder.addCase(
      scheduleDraftPost.fulfilled,
      (
        state: InitialCalendarStateType,
        action: PayloadAction<Post | undefined>
      ) => {
        toast.success("Schedule added successfully");
        state.isLoading = false;
        // Upsert the new scheduled entity so it appears on the calendar
        // immediately, without waiting for the next loadPosts fetch.
        if (action.payload?._id) {
          calendarAdapter.upsertOne(state, action.payload as CalendarPostType);
        }
      }
    );

    builder.addCase(
      scheduleDraftPost.rejected,
      (state: InitialCalendarStateType, { payload }: { payload: any }) => {
        state.isLoading = false;
        toast.error(payload?.response?.data?.error || "There was an error");
      }
    );

    builder.addCase(
      calendarFeedback.pending,
      (state: InitialCalendarStateType) => {
        state.isLoading = true;
      }
    );

    builder.addCase(
      calendarFeedback.fulfilled,
      (state: InitialCalendarStateType) => {
        toast.success("Feedback added successfully");
        state.isLoading = false;
      }
    );

    builder.addCase(
      calendarFeedback.rejected,
      (state: InitialCalendarStateType, { payload }: { payload: any }) => {
        state.isLoading = false;
        toast.error(payload?.response?.data?.error || "There was an error");
      }
    );

    // Handle post status changes from collaboration actions
    // This ensures the calendar UI updates immediately without needing a full refetch
    builder.addCase(
      CHANGE_COLLAB_STATE_ROUTINE.SUCCESS,
      (
        state: InitialCalendarStateType,
        action: {
          payload: { postIds: string[]; postStatusKey: string };
        }
      ) => {
        const { postIds, postStatusKey } = action.payload;
        const updates = postIds
          .filter((id) => state.entities[id])
          .map((id) => ({
            id,
            changes: { postStatusKey },
          }));
        if (updates.length) {
          // @ts-ignore - state type mismatch with entity adapter
          calendarAdapter.updateMany(state, updates);
        }
      }
    );

    builder.addCase(
      BULK_CHANGE_COLLAB_STATE_ROUTINE.SUCCESS,
      (
        state: InitialCalendarStateType,
        action: {
          payload: { postIds: string[]; postStatusKey: string };
        }
      ) => {
        const { postIds, postStatusKey } = action.payload;
        const updates = postIds
          .filter((id) => state.entities[id])
          .map((id) => ({
            id,
            changes: { postStatusKey },
          }));
        if (updates.length) {
          // @ts-ignore - state type mismatch with entity adapter
          calendarAdapter.updateMany(state, updates);
        }
      }
    );
  },
});

export const calendarReducer = calendarSlice.reducer;
export const calendarActions = calendarSlice.actions;
